import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ChevronDown, Pin, Loader2, Tag, Settings2, Plus } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import PulseListModal from "./PulseListModal";
import PulseDetailModal from "./PulseDetailModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import apiClient from "../lib/api";

// Types matching API response
interface CategoryCount {
  id: string;
  name: string;
  count: number;
}

interface StatusCount {
  label: string;
  count: number;
  type: "open" | "decided" | "closed";
}

interface SourceSummary {
  id: string;
  name: string;
  abbr?: string;
  full_name?: string;
  total_count: number;
  categories?: CategoryCount[];
  statuses?: StatusCount[];
}

interface ColumnData {
  title: string;
  total_new: number;
  sources: SourceSummary[];
}

interface DashboardData {
  municipal: ColumnData;
  regional: ColumnData;
  provincial: ColumnData;
}

interface PulseListItem {
  id: number;
  insight_id: number;
  title: string;
  source: string;
  source_type: "municipal" | "regional" | "provincial";
  category: string;
  category_id?: number;
  date: string;
  what_changed: string;
  is_pinned: boolean;
  // New personalized fields
  tldr_short?: string;
  impact?: string; // high, medium, low
  time_sensitivity?: string;
  is_relevant?: boolean;
  /** True when linked insight has meaningful_change_check === 'Needs Review' (hidable in dashboard) */
  needs_review?: boolean;
}

export interface EvidenceRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface InsightEvidenceItem {
  document_id: number;
  page_number: number;
  quote: string;
  highlightable: boolean;
  rects: EvidenceRect[];
}

interface PulseDetail {
  id: number;
  insight_id: number;
  title: string;
  source: string;
  source_type: "municipal" | "regional" | "provincial";
  category: string;
  category_id?: number;
  date: string;
  what_changed: string;
  who_where_affects?: string;
  impact_on_you?: string;
  suggested_action?: string;
  evidence_url?: string;
  status?: string;
  confidence?: number;
  is_relevant?: boolean;
  is_pinned: boolean;
  // New personalized fields
  tldr_short?: string;
  what_changed_bullets?: string[];
  impact?: string; // high, medium, low
  time_sensitivity?: string;
  action_bullets?: string[];
  // Key dates from insight
  item_number?: string;
  published_date?: string;
  meeting_date?: string;
  time_anchor_type?: string;
  time_anchor_date?: string;
  time_anchor_label?: string;
  decision_status?: string;
  // PDF source citations (Phase 4/5)
  document_id?: number;
  evidence?: InsightEvidenceItem[];
  /** True when linked insight has meaningful_change_check === 'Needs Review' (hidable in dashboard) */
  needs_review?: boolean;
  /** True when at least one correction exists for this insight */
  has_correction?: boolean;
  /** True when user hid the pulse after correction; excluded from list/dashboard */
  hidden_after_correction?: boolean;
  /** Full URL to municipality meeting agenda (eSCRIBE Meeting.aspx?Id=...) */
  agenda_url?: string | null;
  /** Meeting start from public_events.event_start (ISO string) */
  event_start?: string | null;
}

interface PinnedPulse {
  id: number;
  pulse_id: number;
  insight_id: number;
  title: string;
  source: string;
  source_type: "municipal" | "regional" | "provincial";
  category: string;
  time_ago: string;
  pinned_at: string;
}

// For compatibility with modals
interface LegacyPulseItem {
  id: string;
  insightId: number;  // Keep track of insight_id for API calls
  title: string;
  source: string;
  sourceType: "municipal" | "regional" | "provincial";
  category: string;
  date: string;
  isPinned?: boolean;
  whatChanged: string;
  whoWhereAffects: string;
  impactOnYou?: string;
  suggestedAction?: string;
  evidenceUrl?: string;
  status: string;
  confidence: number;
  isRelevant?: boolean;
  // New personalized fields
  tldrShort?: string;
  whatChangedBullets?: string[];
  impact?: string; // high, medium, low
  timeSensitivity?: string;
  actionBullets?: string[];
  // Key dates from insight
  itemNumber?: string;
  publishedDate?: string;
  meetingDate?: string;
  timeAnchorType?: string;
  timeAnchorDate?: string;
  timeAnchorLabel?: string;
  decisionStatus?: string;
  // PDF source citations (Phase 5)
  documentId?: number;
  evidence?: InsightEvidenceItem[];
  needsReview?: boolean;
  hasCorrection?: boolean;
  hiddenAfterCorrection?: boolean;
  agendaUrl?: string | null;
  eventStart?: string | null;
}

interface LegacySource {
  id: string;
  name: string;
  abbr?: string;
  fullName?: string;
  categories?: { id: string; name: string; count: number }[];
  statuses?: { label: string; count: number; type: "open" | "decided" | "closed" }[];
  totalCount: number;
}

// Convert API response to legacy format for modals
function convertToLegacyPulse(pulse: PulseDetail | PulseListItem): LegacyPulseItem {
  const detail = pulse as PulseDetail;
  const listItem = pulse as PulseListItem;
  return {
    id: String(pulse.id),
    insightId: pulse.insight_id,  // Keep the insight_id for API calls
    title: pulse.title,
    source: pulse.source,
    sourceType: pulse.source_type,
    category: pulse.category,
    date: pulse.date,
    isPinned: pulse.is_pinned,
    whatChanged: pulse.what_changed,
    whoWhereAffects: detail.who_where_affects || "",
    impactOnYou: detail.impact_on_you,
    suggestedAction: detail.suggested_action,
    evidenceUrl: detail.evidence_url,
    status: detail.status || "pending",
    confidence: detail.confidence || 3,
    isRelevant: detail.is_relevant ?? listItem.is_relevant,
    // New personalized fields
    tldrShort: detail.tldr_short ?? listItem.tldr_short,
    whatChangedBullets: detail.what_changed_bullets ?? (listItem as any).what_changed_bullets ?? null,
    impact: detail.impact ?? listItem.impact,
    timeSensitivity: detail.time_sensitivity ?? listItem.time_sensitivity,
    actionBullets: detail.action_bullets,
    // Key dates from insight
    itemNumber: detail.item_number,
    publishedDate: detail.published_date,
    meetingDate: detail.meeting_date,
    timeAnchorType: detail.time_anchor_type,
    timeAnchorDate: detail.time_anchor_date,
    timeAnchorLabel: detail.time_anchor_label,
    decisionStatus: detail.decision_status,
    documentId: detail.document_id,
    evidence: detail.evidence ?? (listItem as any).evidence ?? undefined,
    needsReview: detail.needs_review ?? listItem.needs_review,
    hasCorrection: detail.has_correction ?? (listItem as any).has_correction,
    hiddenAfterCorrection: detail.hidden_after_correction ?? (listItem as any).hidden_after_correction,
    agendaUrl: detail.agenda_url ?? undefined,
    eventStart: detail.event_start != null ? (typeof detail.event_start === "string" ? detail.event_start : (detail.event_start as any)?.toISO?.() ?? null) : undefined,
  };
}

function convertToLegacySource(source: SourceSummary): LegacySource {
  return {
    id: source.id,
    name: source.name,
    abbr: source.abbr,
    fullName: source.full_name,
    categories: source.categories?.map(c => ({ id: c.id, name: c.name, count: c.count })),
    statuses: source.statuses,
    totalCount: source.total_count,
  };
}

// Column Component
function Column({ 
  title, 
  totalNew, 
  children,
  isLoading 
}: { 
  title: string; 
  totalNew: number; 
  children: React.ReactNode;
  isLoading?: boolean;
}) {
  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg flex flex-col h-full overflow-hidden min-h-0">
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex-shrink-0">
        <span className={`font-semibold text-sm text-neutral-900 dark:text-white ${title === title.toUpperCase() ? 'uppercase tracking-wider' : ''}`}>
          {title}
        </span>
        <Badge className="bg-primary text-white text-[11px] font-semibold px-2 py-0.5">
          {totalNew} new
        </Badge>
      </div>
      <div className="p-4 overflow-y-auto flex-1 min-h-0 space-y-3 custom-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// Build key for "seen" tracking (sourceType_sourceId_categoryIdOrLabel)
function getSeenKey(sourceType: string, sourceId: string, category: CategoryCount | StatusCount): string {
  return "label" in category
    ? `${sourceType}_${sourceId}_${category.label}`
    : `${sourceType}_${sourceId}_${category.id}`;
}

// Source Card Component
function SourceCard({ 
  source, 
  sourceType,
  seenCategoryKeys,
  onCategoryClick 
}: { 
  source: SourceSummary; 
  sourceType: "municipal" | "regional" | "provincial";
  seenCategoryKeys: Set<string>;
  onCategoryClick: (source: SourceSummary, category: CategoryCount | StatusCount) => void;
}) {
  const [topicsExpanded, setTopicsExpanded] = useState(true);

  // Format category: remove dashes, capitalize words
  const formatCategory = (categoryName: string) => {
    return categoryName
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Source-level badge: primary (same as column "new" counts) if any topic underneath is unseen (new), else grey
  const hasAnyUnseen = source.statuses && source.statuses.length > 0
    ? source.statuses.some((s) => !seenCategoryKeys.has(getSeenKey(sourceType, source.id, s)))
    : source.categories?.some((c) => !seenCategoryKeys.has(getSeenKey(sourceType, source.id, c))) ?? false;
  const sourceBadgeClass = hasAnyUnseen
    ? "text-xs bg-primary text-white font-semibold"
    : "text-xs bg-neutral-200 text-neutral-600 dark:bg-neutral-600 dark:text-neutral-200";
  
  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
      {/* Header — source name, count, and clickable arrow to toggle topics */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-neutral-800">
        <span className="font-semibold text-sm text-neutral-900 dark:text-white">
          {source.name}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setTopicsExpanded((prev) => !prev); }}
          className="flex items-center gap-2 rounded-md p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
          aria-expanded={topicsExpanded}
          aria-label={topicsExpanded ? "Hide topics" : "Show topics"}
        >
          <Badge className={cn("font-semibold", sourceBadgeClass)}>
            {source.total_count}
          </Badge>
          {topicsExpanded ? (
            <ChevronDown className="h-4 w-4 text-neutral-400 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
          )}
        </button>
      </div>
      
      {/* Categories or Statuses — hidden when collapsed */}
      {topicsExpanded && (source.statuses && source.statuses.length > 0 ? (
        // Provincial ERO/ORR style with statuses
        source.statuses.map((status, idx) => {
          const isNew = !seenCategoryKeys.has(getSeenKey(sourceType, source.id, status));
          const statusBadgeClass = isNew
            ? "text-xs font-semibold bg-primary text-white"
            : "text-xs font-semibold bg-neutral-200 text-neutral-600 dark:bg-neutral-600 dark:text-neutral-200";
          return (
            <div
              key={idx}
              onClick={() => onCategoryClick(source, status)}
              className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <span>{status.label}</span>
              <Badge className={statusBadgeClass}>
                {status.count}
              </Badge>
            </div>
          );
        })
      ) : source.categories && source.categories.length > 0 ? (
        // Categories style
        source.categories.map((category) => {
          const isNew = !seenCategoryKeys.has(getSeenKey(sourceType, source.id, category));
          const categoryBadgeClass = isNew
            ? "bg-primary text-white text-xs font-semibold"
            : "bg-neutral-200 text-neutral-600 dark:bg-neutral-600 dark:text-neutral-200 text-xs font-semibold";
          return (
            <div
              key={category.id}
              onClick={() => onCategoryClick(source, category)}
              className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <span>{formatCategory(category.name)}</span>
              <Badge className={categoryBadgeClass}>
                {category.count}
              </Badge>
            </div>
          );
        })
      ) : (
        <div className="px-4 py-3 text-sm text-neutral-400 italic">
          No items
        </div>
      ))}
    </div>
  );
}

// Pinned Pulses Section
function PinnedPulsesSection({ 
  pulses, 
  onUnpin, 
  onPulseClick,
  isLoading 
}: { 
  pulses: PinnedPulse[]; 
  onUnpin: (insightId: number) => void;
  onPulseClick: (pulse: PinnedPulse) => void;
  isLoading?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Hide scrollbar after scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg mt-6 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Pin className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm text-neutral-900 dark:text-white">
            Pinned Pulses
          </span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {pulses.length} saved
        </Badge>
      </div>
      <div 
        ref={scrollRef}
        className={`p-2 overflow-y-auto flex-1 min-h-0 pinned-pulses-scroll ${isScrolling ? 'scrolling' : ''}`}
      >
        {pulses.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
            <Pin className="h-8 w-8 text-neutral-300 dark:text-neutral-700 mb-3" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No pinned pulses yet
            </p>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              Pin pulses to save them here
            </p>
          </div>
        ) : (
          pulses.map((pulse) => {
          // Format category: remove dashes, capitalize words
          const formatCategory = (category: string) => {
            return category
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          };
          
          return (
          <div
            key={pulse.id}
            onClick={() => onPulseClick(pulse)}
            className="flex items-start gap-3 px-4 py-3 rounded-md cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUnpin(pulse.insight_id);
              }}
              className="text-neutral-400 hover:text-red-500 transition-colors text-lg leading-none mt-0.5"
            >
              ×
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-neutral-900 dark:text-white mb-1 line-clamp-2">
                {pulse.title}
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <span 
                  className={cn(
                    "text-[11px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded",
                    pulse.source_type === "municipal" && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                    pulse.source_type === "regional" && "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
                    pulse.source_type === "provincial" && "bg-primary/10 text-primary"
                  )}
                >
                  {pulse.source}
                </span>
                <span className="w-1 h-1 bg-neutral-300 rounded-full" />
                <span>{formatCategory(pulse.category)}</span>
                <span className="w-1 h-1 bg-neutral-300 rounded-full" />
                <span>{pulse.time_ago}</span>
              </div>
            </div>
          </div>
          );
          })
        )}
      </div>
    </div>
  );
}

// Followed Categories Interface
interface FollowedCategory {
  id: number;
  slug: string;
  display_name: string;
  description: string;
  is_default: boolean;
  is_custom: boolean;
  pulse_count?: number;
}

// Followed Categories Section
function FollowedCategoriesSection({ 
  onManageClick,
  isLoading 
}: { 
  onManageClick: () => void;
  isLoading?: boolean;
}) {
  // Fetch followed categories
  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: ["categories", "followed"],
    queryFn: () => apiClient.topics.list(),
  });
  
  const followedCategories = categories.filter((cat: FollowedCategory) => cat.is_custom || cat.is_default);
  
  if (isLoading || isCategoriesLoading) {
    return (
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg mt-6 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg mt-6">
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm text-neutral-900 dark:text-white">
            Topics
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {followedCategories.length} followed
          </Badge>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onManageClick}
            className="h-8 text-xs gap-1 text-neutral-500 hover:text-neutral-900"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Manage
          </Button>
        </div>
      </div>
      <div className="p-4">
        {followedCategories.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-neutral-500 mb-3">No categories followed yet.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onManageClick}
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              Add Categories
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {followedCategories.map((category: FollowedCategory) => (
              <div
                key={category.id}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-default",
                  category.is_custom 
                    ? "border-primary/30 bg-primary/5 text-primary-dark"
                    : "border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                )}
              >
                <span className="text-sm font-medium">{category.display_name}</span>
                {category.pulse_count !== undefined && category.pulse_count > 0 && (
                  <Badge className="bg-primary text-white text-[10px] px-1.5 py-0">
                    {category.pulse_count}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Main PulseCenter Component
export default function PulseCenter({ 
  onOpenCategoryModal,
  pulseType = 'business',
  municipalityId = null,
  showNeedsReview = false,
  foundDatePeriod = null
}: { 
  onOpenCategoryModal?: () => void;
  pulseType?: 'business' | 'projects' | 'acquisitions';
  municipalityId?: string | number | null;
  /** When false (default), hide pulses whose insight has meaningful_change_check === 'Needs Review'. When true, show all. */
  showNeedsReview?: boolean;
  /** Filter dashboard by when pulse was found (p.created_at): today, yesterday, last_7_days, last_30_days. null/empty = no filter. */
  foundDatePeriod?: string | null;
}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log("[ShowNeedsReview] PulseCenter received showNeedsReview=", showNeedsReview);
  }, [showNeedsReview]);
  
  const [selectedCategory, setSelectedCategory] = useState<{
    source: SourceSummary;
    category: CategoryCount | StatusCount;
    sourceType: "municipal" | "regional" | "provincial";
  } | null>(null);
  
  const [selectedPulseId, setSelectedPulseId] = useState<number | null>(null);
  const [selectedSourceType, setSelectedSourceType] = useState<"municipal" | "regional" | "provincial">("municipal");
  
  // Unpin confirmation state
  const [unpinConfirmId, setUnpinConfirmId] = useState<number | null>(null);

  // Seen category keys: "sourceType_sourceId_categoryIdOrLabel" — persisted so counts turn grey after first view
  const SEEN_KEYS_STORAGE = "pulseSeenCategoryKeys";
  const [seenCategoryKeys, setSeenCategoryKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(SEEN_KEYS_STORAGE);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        return new Set(Array.isArray(arr) ? arr : []);
      }
    } catch (_) {}
    return new Set();
  });

  const markCategorySeen = React.useCallback((sourceType: string, sourceId: string, category: CategoryCount | StatusCount) => {
    const key = "label" in category
      ? `${sourceType}_${sourceId}_${category.label}`
      : `${sourceType}_${sourceId}_${category.id}`;
    setSeenCategoryKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      try {
        localStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify([...next]));
      } catch (_) {}
      return next;
    });
  }, []);

  // Fetch combined dashboard data (includes stats, dashboard, and pinned). Counts respect Show Needs Review and foundDatePeriod.
  const { data: dashboardResponse, isLoading: isDashboardLoading } = useQuery({
    queryKey: ["pulses", "dashboard", pulseType, municipalityId, showNeedsReview, foundDatePeriod],
    queryFn: async () => {
      const response = await apiClient.pulses.getDashboard(pulseType, {
        include_needs_review: showNeedsReview,
        period: foundDatePeriod || undefined,
      });
      return response;
    },
  });

  // Extract data from combined response
  const rawDashboardData = dashboardResponse?.dashboard;

  // Filter dashboard data by selected municipality and determine which column it belongs to
  const { dashboardData, filteredSourceType } = React.useMemo(() => {
    if (!rawDashboardData || !municipalityId) {
      return { dashboardData: rawDashboardData, filteredSourceType: null };
    }

    // Filter sources to only show the selected municipality/source
    // Handle both numeric IDs (municipalities) and string IDs (provincial sources)
    const filterSources = (sources: any[]) => {
      const filterIdStr = (municipalityId?.toString() || '').toLowerCase().trim();
      
      return sources
        .map((source: any) => {
          const sourceIdStr = (source.id?.toString() || '').toLowerCase().trim();
          const municipalityIdStr = (source.municipality_id?.toString() || '').toLowerCase().trim();
          const filterIdStrLower = filterIdStr.toLowerCase().trim();
          
          // For provincial sources, also try matching by name (case-insensitive)
          // Match the API format: name.lower().replace(' ', '_') which replaces ALL spaces
          // Python's .replace(' ', '_') replaces ALL occurrences, JavaScript equivalent is .replace(/ /g, '_')
          const sourceNameId = source.name?.toLowerCase().replace(/ /g, '_').trim() || '';
          
          // Try multiple matching strategies
          const matchesMunicipality = 
            sourceIdStr === filterIdStrLower ||
            municipalityIdStr === filterIdStrLower ||
            sourceNameId === filterIdStrLower ||
            source.id === municipalityId ||
            source.municipality_id === municipalityId ||
            (typeof municipalityId === 'string' && typeof source.id === 'string' && 
             source.id.toLowerCase().trim() === municipalityId.toLowerCase().trim()) ||
            // Additional fallback: try matching name directly (for provincial sources)
            (source.name?.toLowerCase().trim() === (typeof municipalityId === 'string' ? municipalityId.toLowerCase().replace(/_/g, ' ').trim() : ''));
          
          if (matchesMunicipality) {
            return source;
          }
          return null;
        })
        .filter((source: any) => source !== null);
    };

    // Check which column contains the selected municipality
    const municipalMatch = filterSources(rawDashboardData.municipal?.sources || []).length > 0;
    const regionalMatch = filterSources(rawDashboardData.regional?.sources || []).length > 0;
    const provincialMatch = filterSources(rawDashboardData.provincial?.sources || []).length > 0;


    let sourceType: "municipal" | "regional" | "provincial" | null = null;
    if (municipalMatch) sourceType = "municipal";
    else if (regionalMatch) sourceType = "regional";
    else if (provincialMatch) sourceType = "provincial";

    return {
      dashboardData: {
        municipal: {
          ...rawDashboardData.municipal,
          sources: filterSources(rawDashboardData.municipal?.sources || []),
          total_new: filterSources(rawDashboardData.municipal?.sources || [])
            .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
        },
        regional: {
          ...rawDashboardData.regional,
          sources: filterSources(rawDashboardData.regional?.sources || []),
          total_new: filterSources(rawDashboardData.regional?.sources || [])
            .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
        },
        provincial: {
          ...rawDashboardData.provincial,
          sources: filterSources(rawDashboardData.provincial?.sources || []),
          total_new: filterSources(rawDashboardData.provincial?.sources || [])
            .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
        }
      },
      filteredSourceType: sourceType
    };
  }, [rawDashboardData, municipalityId]);
  const pinnedPulses = dashboardResponse?.pinned || [];
  const isPinnedLoading = isDashboardLoading;

  // Build a stable category key for query caching (include foundDatePeriod so modal list matches dashboard filter)
  const categoryKey = selectedCategory 
    ? `${selectedCategory.source.id}-${selectedCategory.sourceType}-${'name' in selectedCategory.category ? selectedCategory.category.name : selectedCategory.category.label}`
    : null;
  const listPeriod = foundDatePeriod && foundDatePeriod !== "all" ? foundDatePeriod : undefined;

  // Fetch pulse list when category is selected; filtered by same "Pulses found" date as dashboard
  const { data: pulseList = [], isLoading: isPulseListLoading, isFetching: isPulseListFetching } = useQuery({
    queryKey: ["pulses", "list", categoryKey, listPeriod],
    queryFn: async () => {
      if (!selectedCategory) return [];
      const categoryName = 'name' in selectedCategory.category 
        ? selectedCategory.category.name 
        : selectedCategory.category.label;
      console.log("[PulseCenter] Fetching pulse list:", {
        sourceId: selectedCategory.source.id,
        sourceType: selectedCategory.sourceType,
        categoryName,
        period: listPeriod
      });
      const result = await apiClient.pulses.getList(
        selectedCategory.source.id,
        selectedCategory.sourceType,
        categoryName,
        pulseType,
        listPeriod
      );
      console.log("[PulseCenter] Pulse list result:", result);
      return result;
    },
    enabled: !!selectedCategory,
    staleTime: 0, // Always refetch when category or period changes
  });

  // Fetch pulse detail
  const { data: pulseDetail, isLoading: isPulseDetailLoading } = useQuery({
    queryKey: ["pulses", "detail", selectedPulseId, selectedSourceType],
    queryFn: () => {
      if (!selectedPulseId) return null;
      return apiClient.pulses.getDetail(selectedPulseId, selectedSourceType);
    },
    enabled: !!selectedPulseId,
  });

  // Pin mutation
  const pinMutation = useMutation({
    mutationFn: ({ insightId, sourceType }: { insightId: number; sourceType: string }) =>
      apiClient.pulses.pin(insightId, sourceType),
    onSuccess: () => {
      // Invalidate all relevant queries to update counts and lists immediately
      queryClient.invalidateQueries({ queryKey: ["pulses", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "pinned"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "list"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "detail"] });
      // Invalidate dashboard stats to update Action Queue card immediately
      queryClient.invalidateQueries({ queryKey: ["pulse-dashboard-stats"] });
    },
  });

  // Unpin mutation
  const unpinMutation = useMutation({
    mutationFn: (insightId: number) => apiClient.pulses.unpin(insightId),
    onSuccess: () => {
      // Invalidate all relevant queries to update counts and lists immediately
      queryClient.invalidateQueries({ queryKey: ["pulses", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "pinned"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "list"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "detail"] });
      // Invalidate dashboard stats to update Action Queue card immediately
      queryClient.invalidateQueries({ queryKey: ["pulse-dashboard-stats"] });
    },
  });

  // Dismiss mutation - mark pulse as not relevant
  const dismissMutation = useMutation({
    mutationFn: (insightId: number) => apiClient.pulses.dismiss(insightId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pulses", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["pulses", "list"] });
    },
  });

  // When showNeedsReview is false, hide pulses linked to insights with Needs Review (hidable)
  const displayedPulseList = React.useMemo(() => {
    const list = pulseList || [];
    const needsReviewCount = list.filter((p: PulseListItem) => p.needs_review).length;
    // Only log when list is loaded (you must open a source + category to load the list)
    if (list.length > 0) {
      console.log("[ShowNeedsReview] List loaded: total=", list.length, "needsReview=", needsReviewCount, "showNeedsReview=", showNeedsReview, "-> showing", showNeedsReview ? list.length : list.length - needsReviewCount);
    }
    if (showNeedsReview) return list;
    return list.filter((p: PulseListItem) => !p.needs_review);
  }, [pulseList, showNeedsReview]);

  useEffect(() => {
    // Only check when we have a selected category (modal is open) and data has loaded
    if (selectedCategory && !isPulseListLoading && !isPulseListFetching) {
      // If the displayed list is empty and we're not viewing a pulse detail, close the modal
      if (displayedPulseList.length === 0 && !selectedPulseId) {
        setSelectedCategory(null);
      }
    }
  }, [displayedPulseList.length, selectedCategory, selectedPulseId, isPulseListLoading, isPulseListFetching]);

  const handleCategoryClick = (
    source: SourceSummary, 
    category: CategoryCount | StatusCount,
    sourceType: "municipal" | "regional" | "provincial"
  ) => {
    setSelectedCategory({ source, category, sourceType });
  };

  const handlePulseClick = (pulse: LegacyPulseItem) => {
    // Mark this category as seen only when user actually opens a pulse (not just the topic list)
    if (selectedCategory) {
      markCategorySeen(selectedCategory.sourceType, selectedCategory.source.id, selectedCategory.category);
    }
    setSelectedPulseId(pulse.insightId);
    setSelectedSourceType(pulse.sourceType);
  };

  const handleBackToList = () => {
    setSelectedPulseId(null);
  };

  const handleCloseModals = () => {
    setSelectedPulseId(null);
    setSelectedCategory(null);
  };

  const handleTogglePin = (pulse: LegacyPulseItem) => {
    const insightId = pulse.insightId;
    if (pulse.isPinned) {
      unpinMutation.mutate(insightId);
    } else {
      pinMutation.mutate({ insightId, sourceType: pulse.sourceType });
    }
  };

  const handleUnpinFromList = (insightId: number) => {
    // Show confirmation dialog before unpinning
    setUnpinConfirmId(insightId);
  };
  
  const confirmUnpin = () => {
    if (unpinConfirmId !== null) {
      unpinMutation.mutate(unpinConfirmId);
      setUnpinConfirmId(null);
    }
  };

  const handlePinnedPulseClick = (pulse: PinnedPulse) => {
    setSelectedPulseId(pulse.insight_id);
    setSelectedSourceType(pulse.source_type);
  };

  // Get pinned IDs set
  const pinnedIds = new Set<number>(pinnedPulses.map((p: PinnedPulse) => p.insight_id));

  // Convert pulse list to legacy format with pinned status
  const legacyPulseList: LegacyPulseItem[] = displayedPulseList.map((p: PulseListItem) => {
    const legacy = convertToLegacyPulse(p as any);
    return {
      ...legacy,
      isPinned: pinnedIds.has(p.insight_id),
    };
  });
  
  // Debug: log when there's a mismatch between API data and legacy list
  if (displayedPulseList.length > 0 && legacyPulseList.length !== displayedPulseList.length) {
    console.error("[PulseCenter] Mismatch! displayed list has", displayedPulseList.length, "but legacy list has", legacyPulseList.length);
  }

  // Determine if we should show only one column (when a source is selected)
  const showSingleColumn = municipalityId !== null && filteredSourceType !== null;

  // Get the selected source when filtering
  const selectedSource = React.useMemo(() => {
    if (!showSingleColumn || !dashboardData || !filteredSourceType) return null;
    
    const sources = dashboardData[filteredSourceType]?.sources || [];
    return sources.length > 0 ? sources[0] : null;
  }, [showSingleColumn, dashboardData, filteredSourceType]);

  // Format category: remove dashes, capitalize words
  const formatCategory = (categoryName: string) => {
    return categoryName
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0 h-full pulse-center-container">
      {/* Column Grid - Single column when source is selected, three columns otherwise */}
      <div className={`flex-1 grid gap-4 min-h-0 overflow-hidden mb-6 ${showSingleColumn ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
        {/* Municipal Column */}
        {(!showSingleColumn || filteredSourceType === "municipal") && (
          <Column 
            title={showSingleColumn && selectedSource ? selectedSource.name : "Municipal"} 
            totalNew={dashboardData?.municipal?.total_new || 0}
            isLoading={isDashboardLoading}
          >
            {showSingleColumn && selectedSource ? (
              // Show categories directly when a single source is selected
              <>
                {selectedSource.statuses && selectedSource.statuses.length > 0 ? (
                  // Provincial ERO/ORR style with statuses
                  selectedSource.statuses.map((status, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCategoryClick(selectedSource, status, "municipal")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                    >
                      <span>{status.label}</span>
                      <Badge 
                        className={cn(
                          "text-xs font-semibold",
                          status.type === "open" && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                          status.type === "decided" && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                          status.type === "closed" && "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"
                        )}
                      >
                        {status.count}
                      </Badge>
                    </div>
                  ))
                ) : selectedSource.categories && selectedSource.categories.length > 0 ? (
                  // Categories style
                  selectedSource.categories.map((category) => (
                    <div
                      key={category.id}
                      onClick={() => handleCategoryClick(selectedSource, category, "municipal")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors last:border-b-0"
                    >
                      <span>{formatCategory(category.name)}</span>
                      <Badge className="bg-primary text-white text-xs font-semibold">
                        {category.count}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-neutral-400 italic">
                    No items
                  </div>
                )}
              </>
            ) : (
              // Show source cards when multiple sources
              <>
                {dashboardData?.municipal?.sources?.map((source: SourceSummary) => (
                  <SourceCard
                    key={source.id}
                    source={source}
                    sourceType="municipal"
                    seenCategoryKeys={seenCategoryKeys}
                    onCategoryClick={(s, c) => handleCategoryClick(s, c, "municipal")}
                  />
                ))}
                {(!dashboardData?.municipal?.sources || dashboardData.municipal.sources.length === 0) && !isDashboardLoading && (
                  <div className="text-center py-8 text-neutral-400 text-sm">
                    No municipal pulses
                  </div>
                )}
              </>
            )}
          </Column>
        )}

        {/* Regional Column */}
        {(!showSingleColumn || filteredSourceType === "regional") && (
          <Column 
            title={showSingleColumn && selectedSource ? selectedSource.name : "Regional"} 
            totalNew={dashboardData?.regional?.total_new || 0}
            isLoading={isDashboardLoading}
          >
            {showSingleColumn && selectedSource ? (
              // Show categories directly when a single source is selected
              <>
                {selectedSource.statuses && selectedSource.statuses.length > 0 ? (
                  selectedSource.statuses.map((status, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCategoryClick(selectedSource, status, "regional")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                    >
                      <span>{status.label}</span>
                      <Badge 
                        className={cn(
                          "text-xs font-semibold",
                          status.type === "open" && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                          status.type === "decided" && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                          status.type === "closed" && "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"
                        )}
                      >
                        {status.count}
                      </Badge>
                    </div>
                  ))
                ) : selectedSource.categories && selectedSource.categories.length > 0 ? (
                  selectedSource.categories.map((category) => (
                    <div
                      key={category.id}
                      onClick={() => handleCategoryClick(selectedSource, category, "regional")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors last:border-b-0"
                    >
                      <span>{formatCategory(category.name)}</span>
                      <Badge className="bg-primary text-white text-xs font-semibold">
                        {category.count}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-neutral-400 italic">
                    No items
                  </div>
                )}
              </>
            ) : (
              <>
                {dashboardData?.regional?.sources?.map((source: SourceSummary) => (
                  <SourceCard
                    key={source.id}
                    source={source}
                    sourceType="regional"
                    seenCategoryKeys={seenCategoryKeys}
                    onCategoryClick={(s, c) => handleCategoryClick(s, c, "regional")}
                  />
                ))}
                {(!dashboardData?.regional?.sources || dashboardData.regional.sources.length === 0) && !isDashboardLoading && (
                  <div className="text-center py-8 text-neutral-400 text-sm">
                    No regional pulses
                  </div>
                )}
              </>
            )}
          </Column>
        )}

        {/* Provincial Column */}
        {(!showSingleColumn || filteredSourceType === "provincial") && (
          <Column 
            title={showSingleColumn && selectedSource ? selectedSource.name : "Provincial"} 
            totalNew={dashboardData?.provincial?.total_new || 0}
            isLoading={isDashboardLoading}
          >
            {showSingleColumn && selectedSource ? (
              // Show categories directly when a single source is selected
              <>
                {selectedSource.statuses && selectedSource.statuses.length > 0 ? (
                  selectedSource.statuses.map((status, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCategoryClick(selectedSource, status, "provincial")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                    >
                      <span>{status.label}</span>
                      <Badge 
                        className={cn(
                          "text-xs font-semibold",
                          status.type === "open" && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                          status.type === "decided" && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                          status.type === "closed" && "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"
                        )}
                      >
                        {status.count}
                      </Badge>
                    </div>
                  ))
                ) : selectedSource.categories && selectedSource.categories.length > 0 ? (
                  selectedSource.categories.map((category) => (
                    <div
                      key={category.id}
                      onClick={() => handleCategoryClick(selectedSource, category, "provincial")}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors last:border-b-0"
                    >
                      <span>{formatCategory(category.name)}</span>
                      <Badge className="bg-primary text-white text-xs font-semibold">
                        {category.count}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-neutral-400 italic">
                    No items
                  </div>
                )}
              </>
            ) : (
              <>
                {dashboardData?.provincial?.sources?.map((source: SourceSummary) => (
                  <SourceCard
                    key={source.id}
                    source={source}
                    sourceType="provincial"
                    seenCategoryKeys={seenCategoryKeys}
                    onCategoryClick={(s, c) => handleCategoryClick(s, c, "provincial")}
                  />
                ))}
                {(!dashboardData?.provincial?.sources || dashboardData.provincial.sources.length === 0) && !isDashboardLoading && (
                  <div className="text-center py-8 text-neutral-400 text-sm">
                    No provincial pulses
                  </div>
                )}
              </>
            )}
          </Column>
        )}
      </div>

      {/* Pinned Pulses Section - Always at bottom, uses flex-basis for proportional sizing */}
      <div className="flex-shrink-0 w-full" style={{ flexBasis: '28%', minHeight: '200px', maxHeight: '30vh' }}>
        <PinnedPulsesSection
          pulses={pinnedPulses}
          onUnpin={handleUnpinFromList}
          onPulseClick={handlePinnedPulseClick}
          isLoading={isPinnedLoading}
        />
      </div>

      {/* Pulse List Modal */}
      <PulseListModal
        isOpen={!!selectedCategory && !selectedPulseId}
        onClose={handleCloseModals}
        source={selectedCategory ? convertToLegacySource(selectedCategory.source) : undefined}
        category={selectedCategory?.category ? {
          ...selectedCategory.category,
          name: 'name' in selectedCategory.category ? selectedCategory.category.name : selectedCategory.category.label,
        } : undefined}
        pulseItems={legacyPulseList}
        onPulseClick={(pulse) => handlePulseClick(pulse)}
        pinnedIds={pinnedIds}
        onTogglePin={handleTogglePin}
        isLoading={isPulseListLoading || isPulseListFetching}
      />

      {/* Pulse Detail Modal */}
      <PulseDetailModal
        isOpen={!!selectedPulseId}
        onClose={handleCloseModals}
        onBack={handleBackToList}
        pulse={pulseDetail ? convertToLegacyPulse(pulseDetail) : null}
        isPinned={selectedPulseId ? pinnedIds.has(selectedPulseId) : false}
        onTogglePin={() => {
          if (pulseDetail) {
            handleTogglePin(convertToLegacyPulse(pulseDetail));
          }
        }}
        onDismiss={() => {
          if (selectedPulseId) {
            dismissMutation.mutate(selectedPulseId);
          }
        }}
        onPulseHidden={() => {
          queryClient.invalidateQueries({ queryKey: ["pulses", "dashboard"] });
          queryClient.invalidateQueries({ queryKey: ["pulses", "list"] });
          queryClient.invalidateQueries({ queryKey: ["pulses", "pinned"] });
          queryClient.invalidateQueries({ queryKey: ["pulses", "detail"] });
          handleCloseModals();
        }}
        onUnpin={(insightId) => {
          unpinMutation.mutate(insightId);
        }}
        isLoading={isPulseDetailLoading}
      />

      {/* Unpin Confirmation Dialog */}
      <AlertDialog open={unpinConfirmId !== null} onOpenChange={(open) => !open && setUnpinConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Pinned Pulses?</AlertDialogTitle>
            <AlertDialogDescription>
              Once unpinned, this pulse will be removed from your dashboard entirely and cannot be recovered. 
              You won't be able to see this pulse again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Pinned</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnpin}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Unpin & Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
